import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useParams } from 'react-router-dom'

import { Formik, FormikProps } from 'formik'
import moment from 'moment'
import * as Yup from 'yup'

import {
  CompanyNotificationDateInfoResponse,
  getBenefitEligibilityDate,
  getCompanyUserNotificationDateInfo,
} from 'core'
import getLoginUser from 'core/application/account/loginUser'
import { EmploymentStatus } from 'core/application/adminDashboard/managePeople/enums/employmentStatus'
import { ProductIds } from 'core/application/commons/config'
import SaveCompanyUserRequest from 'core/application/companies/common/saveCompanyUserRequest'
import {
  EmployeeForSaveDto,
  ProductClassForEmployeeForSave,
  filterProductClassesByEmploymentStatus,
  getEmployeeForSave,
} from 'core/application/companies/getEmployeeForSave'
import updateEmployee from 'core/application/companies/updateEmployee'
import { NamedItem, NamedItemInt } from 'core/domain/common/namedItem'

import { phoneNumberRegex, zipNumberRegex } from 'common/components/utils/InputUtils'

import {
  Alert,
  Avatar,
  Button,
  Card,
  Category,
  Col,
  DatePicker,
  DefaultDatePickerV2,
  DefaultSelect,
  Icon,
  Label,
  Notification,
  Option,
  Radio,
  RedAsterisk,
  Row,
  SectionBlock,
  Skeleton,
  Tooltip,
} from 'common/components'
import {
  isAfter,
  isBefore,
  isFutureDate,
  now,
  nowAsString,
  parseZoneFormat,
  submitDateToServerFormat,
  toDateWithoutTimeZone,
} from 'common/dateFormatters'
import { FormData, HireDateRadioSelect } from 'common/hireEmployeeUtils'
import { useCommandRequestV2, useCurrentProduct, useLoginUserV2, useQueryRequestV2 } from 'common/hooks'

import EmployeePersonalDetailsForm from '../employeePersonalDetailsForm'

import './styles.less'

type EditEmployeeFormProps = {
  submitHandler?: (values: any) => Promise<void>
  onSubmitted?: () => void
  disableWorkEmailInput?: boolean
  title?: string
  overrideGetEmployeeForSaveHandler?: (companyUserId?: string) => Promise<EmployeeForSaveDto>
}

const EditEmployeeForm = (props: EditEmployeeFormProps) => {
  const commandHandler = props.submitHandler ?? updateEmployee
  const { t: translate } = useTranslation()
  const history = useHistory()
  const { companyUserId } = useParams<any>()
  const product = useCurrentProduct()

  const { user, setUser } = useLoginUserV2()
  const [initialValues, setInitialValues] = useState<FormData | null>(null)
  const [productClasses, setProductClasses] = useState<ProductClassForEmployeeForSave[]>([])
  const [renewalProductClasses, setRenewalProductClasses] = useState<ProductClassForEmployeeForSave[]>([])

  const handler = props.overrideGetEmployeeForSaveHandler ? props.overrideGetEmployeeForSaveHandler : getEmployeeForSave
  const { data: employee } = useQueryRequestV2<EmployeeForSaveDto>({
    initialState: null,
    queryFn: () => handler(companyUserId ?? user.companyUserId),
  })

  const { sendCommand: sendSaveCommand, isLoading: isCommandSaveLoading } = useCommandRequestV2(
    (request: SaveCompanyUserRequest) => commandHandler(request),
  )
  const { isLoading: isRefreshUserLoading, sendQuery: sendRefreshUserRequest } = useQueryRequestV2({
    initialState: null,
  })
  const { sendQuery: sendGetEligibilityDate } = useQueryRequestV2({
    initialState: null,
  })
  const { data: userNotificationDateInfo, sendQuery: sendGetCompanyUserNotificationDateInfo } =
    useQueryRequestV2<CompanyNotificationDateInfoResponse>({
      initialState: {
        isEmployeeNotificationDateEditable: false,
        notificationDate: null,
        maxNotificationDate: null,
        minNotificationDate: nowAsString(),
        requireEmployeeNotificationDate: false,
      },
    })

  const isAdmin = user?.companyUserId === employee?.companyUserId

  useEffect(() => {
    ;(async () => {
      if (employee) {
        setInitialValues({
          ...employee,
          hireDateSelect: null,
          birthDay: employee.birthDay ? submitDateToServerFormat(employee.birthDay) : null,
          benefitHistoryEffectiveDate: employee.currentBenefitHistoryStartDate,
        })
        setProductClasses(
          !employee.employmentStatus
            ? employee.productClasses
            : filterProductClassesByEmploymentStatus(
                employee.productClasses,
                employee.employmentStatus,
                employee.currentCompanyTypeId,
              ),
        )
        setRenewalProductClasses(
          employee.employmentStatus
            ? filterProductClassesByEmploymentStatus(
                employee.renewalProductClasses,
                employee.employmentStatus,
                employee.currentCompanyTypeId,
              )
            : employee.renewalProductClasses,
        )

        await sendGetCompanyUserNotificationDateInfo(() =>
          getCompanyUserNotificationDateInfo({
            companyUserId: employee.companyUserId,
            planPeriodId: employee.planPeriodId,
          }),
        )
      }
    })()
  }, [employee])

  const handleHireDateSelectRadioChange = (e: any, formik: FormikProps<FormData>) => {
    const hireDateSelectValue = e.target.value

    if (hireDateSelectValue === HireDateRadioSelect.IDontKnow) {
      formik.setFieldValue('hireDate', null)
    }
  }

  const submitForm = async (values: any) => {
    const request: SaveCompanyUserRequest = {
      ...values,
      id: companyUserId ?? user.companyUserId,
      hireDate: values.hireDate ?? initialValues.benefitStartDate,
      zipCode: values.zipCode ? values.zipCode.toString() : null,
    }

    const result = await sendSaveCommand(request)

    if (result.completed) {
      const latestUser = await sendRefreshUserRequest(() => getLoginUser())
      setUser(latestUser)

      if (props.onSubmitted) {
        props.onSubmitted()
      } else {
        Notification.open({
          message: translate('admin-dashboard.manage-people.edit-profile.employee-update-success'),
          type: 'success',
        })

        history.goBack()
      }
    }
  }

  const handleEligibilityDateChange = async (
    productClassId: string,
    hireDateSelect: number,
    hireDate: string,
    benefitStartDate: string,
    formik: any,
  ) => {
    if (productClassId) {
      const selectedDate = hireDateSelect === 0 || hireDateSelect === null ? hireDate : benefitStartDate
      const result = await sendGetEligibilityDate(() => getBenefitEligibilityDate(productClassId, selectedDate))

      formik.setFieldValue('benefitEligibilityDate', result)

      /*
       * Benefit effective date cannot be before the employee's hire date.
       * The user should reselect the benefit effective date.
       * */
      if (
        formik.values.benefitHistoryEffectiveDate &&
        isBefore(formik.values.benefitHistoryEffectiveDate, selectedDate)
      ) {
        formik.setFieldValue('benefitHistoryEffectiveDate', null)
      }

      /* This is an edge case.
       * The user initially selects a hire date which results in the notification date to be disabled.
       * Then the user changes the hire date to a date that makes the notification date editable.
       * After that the user changes again the hire date to a date that makes the notification date disabled.
       * In this case we need to define the notification date to today's date.
       * Example of that can be found on the comments section on https://zanebenefits.atlassian.net/browse/IC-274
       * */
      const isEditableBeforeTheNewNotificationInfo = userNotificationDateInfo.isEmployeeNotificationDateEditable

      const notificationDateInfoResponse = await sendGetCompanyUserNotificationDateInfo(() =>
        getCompanyUserNotificationDateInfo({
          companyUserId: employee.companyUserId,
          planPeriodId: employee.planPeriodId,
          overrideBenefitEligibilityDate: result,
        }),
      )

      if (!notificationDateInfoResponse.isEmployeeNotificationDateEditable && isEditableBeforeTheNewNotificationInfo) {
        await formik.setFieldValue('notificationDate', nowAsString())
      }
    } else {
      formik.setFieldValue('benefitEligibilityDate', null)
    }
  }

  return (
    <Row>
      <div className="p-16 bg-white flex align-center w-100">
        <Button
          type="default"
          size="small"
          className="mr-8"
          onClick={() => {
            history.goBack()
          }}
        >
          <Icon type="fa fa-arrow-left" fontAwesome />
        </Button>
        <p className="mb-0 font-16">
          &nbsp;
          {props.title ?? translate('admin-dashboard.manage-people.complete-profile.header-title')}
        </p>
      </div>

      <Col span={24}>
        <Card>
          <Skeleton loading={!initialValues}>
            {initialValues && (
              <Formik
                enableReinitialize={true}
                validationSchema={formValidator(translate, initialValues, productClasses, renewalProductClasses)}
                initialValues={initialValues}
                onSubmit={() => {}}
              >
                {(formik) => (
                  <form className="form-manage-people-wrapper pt-0" onSubmit={formik.handleSubmit}>
                    {initialValues && (
                      <>
                        <Row className="mb-8">
                          <Col md={12} lg={8} className="flex align-center lg-justify-end pr-16">
                            <Avatar
                              className="avatar-background"
                              shape="circle"
                              size="default"
                              children={formik.values.firstName[0] + formik.values.lastName[0]}
                            />
                            <p className="profile-name font-20 t-fullname">
                              {formik.values.firstName} {formik.values.lastName}
                            </p>
                          </Col>

                          <Col md={12} lg={16} className="flex align-center left_border p-16 t-status-eligibility-wrap">
                            <p className="pr-8 status-eligible t-status-eligibility-text">
                              {translate('admin-dashboard.manage-people.complete-profile.eligibility-header')}

                              {formik.values.employmentStatus == null && (
                                <span className="pl-4 value-color t-status-eligibility-value">
                                  {translate('admin-dashboard.manage-people.complete-profile.not-set')}
                                </span>
                              )}

                              {formik.values.employmentStatus != null && (
                                <span className="pl-4 value-color t-status-eligibility-value">
                                  {formik.values.productClassId
                                    ? translate('admin-dashboard.manage-people.complete-profile.eligible')
                                    : translate('admin-dashboard.manage-people.complete-profile.not-eligible')}
                                </span>
                              )}

                              <Tooltip
                                title={translate('admin-dashboard.manage-people.complete-profile.tooltip-2')}
                                position="right"
                              >
                                <Icon type="far fa-question-circle" className="info_icon ml-8" fontAwesome />
                              </Tooltip>
                            </p>
                          </Col>
                        </Row>

                        <SectionBlock
                          className="mb-24"
                          titleElement={
                            <>
                              <Icon className="far fa-user mr-8" />
                              <Category title={translate('admin-dashboard.manage-people.complete-profile.header-1')} />
                            </>
                          }
                        >
                          <EmployeePersonalDetailsForm disableWorkEmailInput={props.disableWorkEmailInput} />
                        </SectionBlock>

                        <SectionBlock
                          className="mb-24"
                          titleElement={
                            <>
                              <Icon className="far fa-suitcase mr-8" />
                              <Category
                                title={translate('admin-dashboard.manage-people.complete-profile.employment-details')}
                              />
                            </>
                          }
                        >
                          {initialValues.canEditBenefitData && (
                            <Row>
                              <Col span={24} className="section-block_content-item">
                                <Alert
                                  className={'t-alert-info'}
                                  type="info"
                                  showIcon
                                  message={translate('admin-dashboard.manage-people.complete-profile.alert-header')}
                                  description={translate(
                                    'admin-dashboard.manage-people.complete-profile.alert-content',
                                  )}
                                />
                              </Col>
                            </Row>
                          )}
                          {initialValues.hireDate == null && (
                            <Row>
                              <Col span={24} className="section-block_content-item">
                                <div className={'mb-8'}>
                                  <RedAsterisk className={'mr-4'} />
                                  <Label className="mr-8">
                                    {translate('admin-dashboard.manage-people.edit-profile.hire-date-label')}
                                  </Label>
                                  <Tooltip
                                    title={
                                      'This is the date the employee was hired. It is used to determine when an eligible employee can start using the benefit. Waiting periods are triggered off of the hire date and can also affect when an employee’s benefit starts.'
                                    }
                                    position="right"
                                  >
                                    <Icon type="far fa-question-circle" className="info_icon" fontAwesome />
                                  </Tooltip>
                                </div>
                                <Radio.Group
                                  disabled={!initialValues.canEditBenefitData}
                                  name="hireDateSelect"
                                  value={formik.values.hireDateSelect}
                                  onRadioChange={(e) => handleHireDateSelectRadioChange(e, formik)}
                                  // @ts-ignore
                                  error={formik.errors.hireDate}
                                  onChange={(e) =>
                                    handleEligibilityDateChange(
                                      formik.values.productClassId,
                                      e.target.value,
                                      formik.values.hireDate?.toString(),
                                      formik.values.benefitStartDate?.toString(),
                                      formik,
                                    )
                                  }
                                >
                                  <Row>
                                    <Col span={24}>
                                      <div className="flex">
                                        <Radio value={0} className="hire_date_radio"></Radio>
                                        <DatePicker
                                          name="hireDate"
                                          placeholder={translate(
                                            'admin-dashboard.manage-people.edit-profile.hire-date-placeholder',
                                          )}
                                          disabledDate={(current) => {
                                            return isAdmin && isFutureDate(current)
                                          }}
                                          id="hireDate"
                                          noStyles
                                          defaultValue={
                                            formik.values.hireDate !== null ? moment(formik.values.hireDate) : undefined
                                          }
                                          disabled={formik.values.hireDateSelect !== HireDateRadioSelect.Date}
                                          hideError={true}
                                          onChange={(e) => {
                                            handleEligibilityDateChange(
                                              formik.values.productClassId,
                                              formik.values.hireDateSelect,
                                              e.toString(),
                                              formik.values.benefitStartDate?.toString(),
                                              formik,
                                            )
                                          }}
                                          allowClear={false}
                                        />
                                      </div>
                                    </Col>
                                    <Col span={24}>
                                      <Radio value={1} className="hire_date_radio pl">
                                        <span className="font-14 mr-8">
                                          {translate('admin-dashboard.manage-people.hire-date-msg')}
                                          <b>{parseZoneFormat(initialValues.benefitStartDate)}</b>
                                        </span>
                                      </Radio>
                                    </Col>
                                  </Row>
                                </Radio.Group>
                              </Col>
                            </Row>
                          )}

                          {initialValues.hireDate && (
                            <Row>
                              <Col span={12} className={'mb-16'}>
                                <div className="mb-8">
                                  <RedAsterisk className={'mr-4'} />
                                  <Label className="mr-8">
                                    {translate('admin-dashboard.manage-people.edit-profile.hire-date-label')}
                                  </Label>
                                  <Tooltip
                                    title={
                                      'This is the date the employee was hired. It is used to determine when an eligible employee can start using the benefit. Waiting periods are triggered off of the hire date and can also affect when an employee’s benefit starts.'
                                    }
                                    position="right"
                                  >
                                    <Icon type="far fa-question-circle" className="info_icon" fontAwesome />
                                  </Tooltip>
                                </div>

                                <DatePicker
                                  disabled={!initialValues.canEditBenefitData}
                                  name="hireDate"
                                  placeholder={translate(
                                    'admin-dashboard.manage-people.edit-profile.hire-date-placeholder',
                                  )}
                                  id="hireDate"
                                  noStyles
                                  defaultValue={
                                    formik.values.hireDate !== null
                                      ? toDateWithoutTimeZone(formik.values.hireDate)
                                      : undefined
                                  }
                                  disabledDate={(current) => {
                                    return isAdmin && isFutureDate(current)
                                  }}
                                  hideError={true}
                                  allowClear={false}
                                  onChange={(e) => {
                                    formik.values.productClassId &&
                                      handleEligibilityDateChange(
                                        formik.values.productClassId,
                                        formik.values.hireDateSelect,
                                        e.toString(),
                                        formik.values.benefitStartDate?.toString(),
                                        formik,
                                      )
                                  }}
                                />
                              </Col>
                            </Row>
                          )}

                          <Row>
                            <Col sm={24} md={12} className="section-block_content-item">
                              <DefaultSelect
                                disabled={!initialValues.canEditBenefitData}
                                onChange={async (newEmploymentStatus) => {
                                  const availableProductClasses = filterProductClassesByEmploymentStatus(
                                    initialValues.productClasses,
                                    newEmploymentStatus,
                                    initialValues.currentCompanyTypeId,
                                  )
                                  setProductClasses(availableProductClasses)

                                  if (availableProductClasses.length == 0) {
                                    formik.setFieldValue('productClassId', null)
                                    await handleEligibilityDateChange(
                                      null,
                                      formik.values.hireDateSelect,
                                      formik.values.hireDate?.toString(),
                                      formik.values.benefitStartDate?.toString(),
                                      formik,
                                    )
                                  }

                                  const availableRenewalClasses = filterProductClassesByEmploymentStatus(
                                    initialValues.renewalProductClasses,
                                    newEmploymentStatus,
                                    initialValues.currentCompanyTypeId,
                                  )
                                  setRenewalProductClasses(availableRenewalClasses)

                                  if (availableRenewalClasses.length == 0) {
                                    formik.setFieldValue('renewalProductClassId', null)
                                  }

                                  if (!initialValues.allowProductClassSelection && availableProductClasses.length > 0) {
                                    formik.setFieldValue('productClassId', availableProductClasses[0].id)
                                    await handleEligibilityDateChange(
                                      availableProductClasses[0].id,
                                      formik.values.hireDateSelect,
                                      formik.values.hireDate?.toString(),
                                      formik.values.benefitStartDate?.toString(),
                                      formik,
                                    )
                                  }

                                  if (!initialValues.allowProductClassSelection && availableRenewalClasses.length > 0) {
                                    formik.setFieldValue('renewalProductClassId', availableRenewalClasses[0].id)
                                  }
                                }}
                                showRequiredSign={true}
                                name="employmentStatus"
                                placeholder={'Select employment status'}
                                label={translate('admin-dashboard.manage-people.edit-profile.employment-status-label')}
                              >
                                {formik.values.employmentStatuses.map((e: NamedItemInt) => (
                                  <Option key={e.id} value={e.id}>
                                    {e.name}
                                  </Option>
                                ))}
                              </DefaultSelect>
                            </Col>

                            <Col sm={24} md={12} className="section-block_content-item">
                              {initialValues.allowProductClassSelection &&
                                productClasses.length > 0 &&
                                formik.values.employmentStatus != null && (
                                  <DefaultSelect
                                    disabled={!initialValues.canEditBenefitData}
                                    showRequiredSign={true}
                                    defaultValue={formik.values.productClassId ? [formik.values.productClassId] : null}
                                    name="productClassId"
                                    placeholder={'Select benefit class'}
                                    label={'Benefit class'}
                                  >
                                    {formik.values.productClasses.map((p: NamedItem) => (
                                      <Option key={p.id} value={p.id}>
                                        {p.name}
                                      </Option>
                                    ))}
                                  </DefaultSelect>
                                )}
                            </Col>

                            {(initialValues.employmentStatus !== null ||
                              (initialValues.productClassId !== null &&
                                formik.values.productClassId !== initialValues.productClassId)) && (
                              <Col sm={24} md={12} className="section-block_content-item">
                                <DatePicker
                                  disabled={!initialValues.canEditBenefitData}
                                  showRequiredSign={true}
                                  label={translate(
                                    'admin-dashboard.manage-people.edit-profile.effective-date-change-label',
                                  )}
                                  className="width-100-perc pr-8"
                                  value={
                                    formik.values.benefitHistoryEffectiveDate
                                      ? toDateWithoutTimeZone(formik.values.benefitHistoryEffectiveDate)
                                      : undefined
                                  }
                                  name="benefitHistoryEffectiveDate"
                                  id="benefitHistoryEffectiveDate"
                                  placeholder={translate(
                                    'admin-dashboard.manage-people.edit-profile.select-date-placeholder',
                                  )}
                                  disabledDate={(d) =>
                                    !d ||
                                    d < toDateWithoutTimeZone(employee.benefitStartDate) ||
                                    d > toDateWithoutTimeZone(employee.benefitEndDate) ||
                                    d < toDateWithoutTimeZone(formik.values.hireDate)
                                  }
                                  noStyles
                                />
                              </Col>
                            )}
                            {initialValues.showEmployeeNotificationDate &&
                              productClasses.length > 0 &&
                              formik.values.employmentStatus != null && (
                                <Col sm={24} md={12} className="section-block_content-item">
                                  <DefaultDatePickerV2
                                    name="notificationDate"
                                    placeholder={'MM/DD/YYYY'}
                                    id="notificationDate"
                                    showRequiredSign={true}
                                    disabled={!userNotificationDateInfo.isEmployeeNotificationDateEditable}
                                    disabledDate={(current) =>
                                      current &&
                                      (isBefore(current, userNotificationDateInfo.minNotificationDate) ||
                                        !userNotificationDateInfo.maxNotificationDate ||
                                        isAfter(current, userNotificationDateInfo.maxNotificationDate))
                                    }
                                    label="Notification Date"
                                  />
                                </Col>
                              )}
                          </Row>

                          {formik.values?.benefitEligibilityDate && (
                            <Row>
                              <Col span={24} className="section-block_content-item">
                                <Alert
                                  className={'t-alert-info'}
                                  type="info"
                                  showIcon
                                  message="Waiting Period Policy"
                                  description="This user will be affected by waiting period policy. Please check the calculated benefit eligibility date below."
                                />
                              </Col>
                            </Row>
                          )}

                          <Row className="mb-24">
                            <Col span={24}>
                              {formik.values?.benefitEligibilityDate && (
                                <Row>
                                  <Col span={24} className="section-block_content-item mb-0">
                                    <p className="font-14 mb-0">
                                      <span className="mr-8">
                                        {translate(
                                          'admin-dashboard.manage-people.edit-profile.benefit-eligibility-date-label',
                                        )}
                                      </span>
                                      <Tooltip
                                        title={
                                          ProductIds.QsehraId === product.productId
                                            ? translate(
                                                'admin-dashboard.manage-people.edit-profile.benefit-eligibility-date-tooltip',
                                              )
                                            : translate('admin-dashboard.manage-people.complete-profile.tooltip-2')
                                        }
                                        position="right"
                                      >
                                        <Icon type="far fa-question-circle" className="info_icon" fontAwesome />
                                      </Tooltip>
                                    </p>
                                    <p className="mb-0">{parseZoneFormat(formik.values.benefitEligibilityDate)}</p>
                                  </Col>
                                </Row>
                              )}
                            </Col>
                          </Row>

                          <Row>
                            <Col span={24}>
                              <Row>
                                <Col className="left-border pl-24" span={15}>
                                  {initialValues.allowProductClassSelection &&
                                    renewalProductClasses.length > 0 &&
                                    formik.values.employmentStatus != null && (
                                      <Row>
                                        <Col sm={24} md={12} className="section-block_content-item mb-0">
                                          <DefaultSelect
                                            name="renewalProductClassId"
                                            placeholder={translate(
                                              'admin-dashboard.manage-people.edit-profile.renewal-employee-class-placeholder',
                                            )}
                                            label={translate(
                                              'admin-dashboard.manage-people.edit-profile.renewal-employee-class-label',
                                            )}
                                          >
                                            {formik.values.renewalProductClasses.map((p: NamedItem) => (
                                              <Option key={p.id} value={p.id}>
                                                {p.name}
                                              </Option>
                                            ))}
                                          </DefaultSelect>
                                        </Col>
                                      </Row>
                                    )}
                                </Col>
                              </Row>
                            </Col>
                          </Row>
                        </SectionBlock>

                        {initialValues.familyStatuses.length > 0 && (
                          <SectionBlock
                            titleElement={
                              <>
                                <Icon className="far fa-users mr-8" />
                                <Category
                                  title={translate('admin-dashboard.manage-people.complete-profile.header-3')}
                                />
                              </>
                            }
                          >
                            <Row>
                              <Col sm={24} md={12} className="section-block_content-item">
                                <DefaultSelect
                                  disabled={!initialValues.canEditBenefitData}
                                  showRequiredSign={true}
                                  className="margin-top-none"
                                  value={
                                    formik.values.familyStatusId ? [formik.values.familyStatusId.toString()] : undefined
                                  }
                                  name="familyStatusId"
                                  label={translate('admin-dashboard.manage-people.edit-profile.family-status-label')}
                                  labelTooltip={translate(
                                    'admin-dashboard.manage-people.edit-profile.family-status-tooltip',
                                  )}
                                  placeholder={translate(
                                    'admin-dashboard.manage-people.edit-profile.family-status-placeholder',
                                  )}
                                >
                                  {initialValues.familyStatuses.map((f: NamedItem) => (
                                    <Option key={f.id.toString()} value={f.id.toString()}>
                                      {f.name}
                                    </Option>
                                  ))}
                                </DefaultSelect>
                              </Col>
                            </Row>

                            {initialValues.familyStatusId !== null &&
                              formik.values.familyStatusId !== initialValues.familyStatusId && (
                                <Row>
                                  <Col sm={24} md={12} className="section-block_content-item mb-24">
                                    <DatePicker
                                      showRequiredSign={true}
                                      label={translate(
                                        'admin-dashboard.manage-people.edit-profile.effective-date-change-label',
                                      )}
                                      className="width-100-perc pr-8"
                                      name="familyStatusEffectiveDate"
                                      id="familyStatusEffectiveDate"
                                      placeholder={translate(
                                        'admin-dashboard.manage-people.edit-profile.select-date-placeholder',
                                      )}
                                      disabledDate={(d) => d > now()}
                                      noStyles
                                    />
                                  </Col>
                                </Row>
                              )}
                          </SectionBlock>
                        )}

                        <Row className="pt-24">
                          <Col className="flex flex-end" span={24}>
                            <Button
                              className={'t-cancel-btn'}
                              onClick={() => {
                                history.goBack()
                              }}
                            >
                              {translate('general.cancel')}
                            </Button>

                            <Button
                              onClick={async () => {
                                await submitForm(formik.values)
                              }}
                              type="primary"
                              className="ml-8 t-submit-form-btn"
                              loading={isCommandSaveLoading || isRefreshUserLoading}
                              disabled={
                                !formValidator(translate, employee, productClasses, renewalProductClasses).isValidSync(
                                  formik.values,
                                ) ||
                                isCommandSaveLoading ||
                                isRefreshUserLoading
                              }
                            >
                              {translate('general.save')}
                            </Button>
                          </Col>
                        </Row>
                      </>
                    )}
                  </form>
                )}
              </Formik>
            )}
          </Skeleton>
        </Card>
      </Col>
    </Row>
  )
}

const formValidator = (translate: any, initialValues: EmployeeForSaveDto, productClasses, renewalProductClasses) =>
  Yup.object({
    firstName: Yup.string().required(translate('general.required')),
    lastName: Yup.string().required(translate('general.required')),
    zipCode: Yup.string()
      .trim()
      .nullable()
      .matches(zipNumberRegex, translate('admin-signup.company-details.errors.zip-number-regex'))
      .required(translate('general.required')),

    workEmail: Yup.string()
      .nullable()
      .email(translate('admin-signup.signup.errors.email'))
      .required(translate('general.required')),
    personalEmail: Yup.string().nullable().email(translate('admin-signup.signup.errors.email')),
    birthDay: Yup.string()
      .nullable()
      .when('requireBirthDate', {
        is: (requireBirthDate: boolean) => requireBirthDate,
        then: Yup.string().required(translate('general.required')),
      }),
    workPhone: Yup.string()
      .transform((curr, orig) => (orig === '' ? null : curr))
      .nullable()
      .matches(phoneNumberRegex, translate('admin-signup.company-details.errors.phone-number-regex'))
      .when('receiveSms', {
        is: (receiveSms: boolean) => receiveSms,
        then: Yup.string().required(' '),
      }),
    stateOfResidenceId: Yup.string().nullable().required(translate('general.required')),
    receiveSms: Yup.boolean().nullable().required(translate('general.required')),
    familyStatusId: Yup.number()
      .nullable()
      .when('familyStatuses', {
        is: (familyStatuses: NamedItem[]) => familyStatuses.length > 0,
        then: Yup.number().required(translate('general.required')),
      }),
    productClassId: Yup.string()
      .nullable()
      .when('employmentStatus', {
        is: (employmentStatus: EmploymentStatus) => productClasses.length > 0,
        then: Yup.string().required(translate('general.required')),
      }),
    employmentStatus: Yup.number().nullable().required(translate('general.required')),
    hireDateSelect: Yup.number()
      .nullable()
      .when('ref', {
        is: () => initialValues.hireDate === null,
        then: Yup.number().required(translate('general.required')),
      }),
    hireDate: Yup.string()
      .nullable()
      .when('hireDateSelect', {
        is: (hireDateSelect: number) => hireDateSelect === HireDateRadioSelect.Date,
        then: Yup.string().required(translate('general.required')),
      }),
    notificationDate: Yup.string()
      .nullable()
      .when('employmentStatus', {
        is: (employmentStatus: EmploymentStatus) =>
          productClasses.length > 0 && initialValues.showEmployeeNotificationDate,
        then: Yup.string().required(translate('general.required')),
      }),
    renewalProductClassId: Yup.string()
      .nullable()
      .when('employmentStatus', {
        is: (employmentStatus: EmploymentStatus) => renewalProductClasses.length > 1,
        then: Yup.string().required(translate('general.required')),
      }),
    benefitHistoryEffectiveDate: Yup.date()
      .nullable()
      .when('employmentStatus', {
        is: (employmentStatus: EmploymentStatus) =>
          moment(initialValues.benefitStartDate).isSameOrBefore(moment(), 'day') &&
          initialValues.employmentStatus !== null,
        then: Yup.date().required(translate('general.required')),
      })
      .when('productClassId', {
        is: (productClassId: string) =>
          moment(initialValues.benefitStartDate).isSameOrBefore(moment(), 'day') &&
          initialValues.productClassId !== productClassId &&
          initialValues.productClassId !== null,
        then: Yup.date().required(translate('general.required')),
      }),
    familyStatusEffectiveDate: Yup.date()
      .nullable()
      .when('familyStatusId', {
        is: (familyStatusId: number) =>
          moment(initialValues.benefitStartDate).isSameOrBefore(moment(), 'day') &&
          initialValues.familyStatusId !== familyStatusId &&
          initialValues.familyStatusId !== null,
        then: Yup.date().required(translate('general.required')),
      }),
  })

export default EditEmployeeForm
